/**
 * @module Model.LessonRate
 *
 */
import { Model } from "typedefs";

export interface LessonRateResponse {
  id: number;
  coachProfileId: string;
  defaultRate: boolean;
  duration: string;
  durationInMinutes: number;
  location: string;
  rate: number;
  title: string;
  onlinePaymentsActive: boolean;
  collectType: string;
  onlineDefault: boolean;
  /**
   * CN-2573: the coach's own words for this option. The payload has carried it since
   * that ticket — `LessonRatePresenter#to_full_json` — but nothing mapped it here, so
   * every screen reading this model saw an option with no description (CFTB-3457).
   */
  description?: string;
  // Facility-level booking options fields
  active?: boolean;
  ownerId?: string;
  ownerType?: string; // "CoachProfile" | "Facility"
  capacity?: number | null;
  bookingsLimit?: number | null; // Maximum future bookings allowed (null = unlimited)
  remainingBookings?: number | null; // Remaining bookings allowed (0 = limit reached, null = unlimited)
  availablePaymentMethods?: string[];
  orderNumber?: number;
  // CN-2711: private clubs book guests without exposing the member price — the
  // option's details still render everywhere, only the price drops out.
  hidePrice?: boolean;
  // Space template applied to the space auto-created for an athlete new to the
  // coach when they book this option (CN-2595); null = no template linked.
  spaceTemplateId?: string | null;
  // Calendar colour coding: sessions booked against this option render in this
  // colour. One of CALENDAR_COLORS, or null for the default.
  color?: string | null;
  // The entries this option names: single resources, and whole sections. They
  // form one list of alternatives, and a booking takes one member.
  scheduleResourceIds?: number[];
  resourceCategoryIds?: number[];
}

export class LessonRateModel implements Model {
  id: number;
  coachProfileId: string;
  defaultRate: boolean;
  duration: string;
  durationInMinutes: number;
  location: string;
  rate: number;
  title: string;
  onlinePaymentsActive: boolean;
  collectType: string;
  onlineDefault: boolean;
  description?: string;
  // Facility-level booking options fields
  active?: boolean;
  ownerId?: string;
  ownerType?: string;
  capacity?: number | null;
  bookingsLimit?: number | null;
  remainingBookings?: number | null;
  availablePaymentMethods?: string[];
  orderNumber?: number;
  hidePrice?: boolean;
  spaceTemplateId?: string | null;
  color?: string | null;
  scheduleResourceIds?: number[];
  resourceCategoryIds?: number[];

  constructor(lessonRate: LessonRateResponse) {
    this.id = lessonRate.id;
    this.coachProfileId = lessonRate.coachProfileId;
    this.defaultRate = lessonRate.defaultRate;
    this.duration = lessonRate.duration;
    this.durationInMinutes = lessonRate.durationInMinutes;
    this.location = lessonRate.location;
    this.rate = lessonRate.rate;
    this.title = lessonRate.title;
    this.onlinePaymentsActive = lessonRate.onlinePaymentsActive;
    this.collectType = lessonRate.collectType;
    this.onlineDefault = lessonRate.onlineDefault;
    this.description = lessonRate.description;
    // Facility-level booking options fields
    this.active = lessonRate.active;
    this.ownerId = lessonRate.ownerId;
    this.ownerType = lessonRate.ownerType;
    this.capacity = lessonRate.capacity;
    this.bookingsLimit = lessonRate.bookingsLimit;
    this.remainingBookings = lessonRate.remainingBookings;
    this.availablePaymentMethods = lessonRate.availablePaymentMethods;
    this.orderNumber = lessonRate.orderNumber;
    this.hidePrice = lessonRate.hidePrice;
    this.spaceTemplateId = lessonRate.spaceTemplateId;
    this.color = lessonRate.color ?? null;
    this.scheduleResourceIds = lessonRate.scheduleResourceIds || [];
    this.resourceCategoryIds = lessonRate.resourceCategoryIds || [];
  }
}
